// Shared handling of event ticket purchases.
//
// Removing a ticket from a person is a soft cancel, not a delete: eventtickets-cancelpurchase
// stamps state: 'cancelled' on the purchase and leaves payments[] and pdfs[] alone, so the
// money history survives. That only works if every reader of purchases[] skips the cancelled
// ones - miss one and a "removed" ticket still counts somewhere, or still opens a door.
//
// Readers: PersonEventTickets (the person's list), EventTicketParticipants (the door list),
// EventTicketsContainer (sold count, revenue and the sales timeline).

import moment from 'moment'

export const PURCHASE_CANCELLED = 'cancelled'

export const isCancelledPurchase = (purchase) => !!purchase && purchase.state === PURCHASE_CANCELLED

// The purchases of an event ticket that still count: everything not cancelled.
export const activePurchases = (eventticket) =>
  ((eventticket && eventticket.purchases) || []).filter((p) => !isCancelledPurchase(p))

// Whether a purchase line takes a ticket, i.e. counts as sold. This is the rule the backend's
// availability check in eventtickets-addpurchases uses (falcon 10fb774), so "myyty / saatavilla"
// here agrees with what the backend will still sell. A line takes a ticket unless it is cancelled,
// or it is unpaid AND its validuntil has passed: a web order gets validuntil = now + 10 min (UTC)
// and no payment until it is paid, so an abandoned cart stays in purchases[] forever. "Paid" means
// payments[] has an entry with the same paymenttoken. An unpaid line with no validuntil (an admin
// sale without a payment) still takes a ticket.
//
// Use soldPurchases for counts against ticketsavailable; keep activePurchases for listing orders.
const paidTokens = (eventticket) =>
  new Set(((eventticket && eventticket.payments) || []).map((p) => p && p.paymenttoken).filter(Boolean))

export const takesTicket = (purchase, paid, now = moment.utc()) => {
  if (!purchase || isCancelledPurchase(purchase)) return false
  if (purchase.paymenttoken && paid.has(purchase.paymenttoken)) return true
  if (!purchase.validuntil) return true
  const until = moment.utc(purchase.validuntil, ['YYYY-MM-DD HH:mm:ssZ', 'YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DD HH:mm'])
  return !until.isValid() || until.isAfter(now)
}

export const soldPurchases = (eventticket) => {
  const paid = paidTokens(eventticket)
  return ((eventticket && eventticket.purchases) || []).filter((p) => takesTicket(p, paid))
}

// Tickets per variation id, by soldPurchases.
export const soldCountByVariation = (eventticket) =>
  soldPurchases(eventticket).reduce((acc, p) => {
    acc[p.variationId] = (acc[p.variationId] || 0) + (p.quantity || 1)
    return acc
  }, {})

// Cancelling works on a whole order, never on a single line of one. The pdf ticket is
// generated per paymenttoken and lists every line of the order, so cancelling one line
// would leave the customer holding a pdf that still shows it. Real data has orders of up
// to five lines, and orders carrying the same variation twice - which is also why a line
// cannot be addressed by paymenttoken + variationId in the first place.
export const orderPurchases = (eventticket, paymenttoken) =>
  paymenttoken ? activePurchases(eventticket).filter((p) => p.paymenttoken === paymenttoken) : []

// How many tickets an order is worth: lines can carry a quantity above one.
export const orderTicketCount = (eventticket, paymenttoken) =>
  orderPurchases(eventticket, paymenttoken).reduce((sum, p) => sum + (p.quantity || 1), 0)

// Collapses several event tickets into one synthetic ticket, so a whole eventticketgroup can
// be reported with the SAME functions a single event uses - getSalesBreakdown and
// getSalesTimeline in EventTicketsContainer both work on this unchanged. Takes tickets after
// filterData has enriched them, i.e. carrying soldcount/soldamount per variation.
//
// Variations merge by NAME, not by id: ids are generated per event, so 'Päivälippu A-katsomo'
// on Friday and on Saturday are different ids for what a manager reads as one product. The
// assumption that the name identifies the product is what makes the whole report meaningful,
// and it is visible in the output - the breakdown lists the merged names.
//
// Purchases are rewritten onto the merged variation ids, because getSalesTimeline keys its
// per variation euro series on variationId and would otherwise draw one line per event per
// variation instead of one per variation. Payments are only ever matched by paymenttoken, so
// they are concatenated untouched.
export const mergeTicketsForReporting = (tickets) => {
  const list = tickets || []
  const byName = new Map()
  const idToMerged = new Map()

  list.forEach((ticket) => {
    ;((ticket && ticket.variations) || []).forEach((v) => {
      const name = v.name || 'Lippu'
      if (!byName.has(name)) {
        byName.set(name, {
          id: 'merged-' + byName.size,
          name,
          section: v.section || '',
          price: v.price,
          soldcount: 0,
          soldamount: 0,
          ticketsavailable: 0,
        })
      }
      const merged = byName.get(name)
      // First contributor wins for section - variations sharing a name are expected to share
      // a sector, and the summary would be nonsense if they did not.
      if (!merged.section && v.section) merged.section = v.section
      merged.soldcount += v.soldcount || 0
      merged.soldamount += v.soldamount || 0
      merged.ticketsavailable += v.ticketsavailable || 0
      if (v.id) idToMerged.set(v.id, merged.id)
    })
  })

  const purchases = []
  const payments = []
  list.forEach((ticket) => {
    ;((ticket && ticket.purchases) || []).forEach((p) => {
      purchases.push({ ...p, variationId: idToMerged.get(p.variationId) || p.variationId })
    })
    ;((ticket && ticket.payments) || []).forEach((pay) => payments.push(pay))
  })

  const variations = [...byName.values()]

  // Totals come from the merged VARIATIONS, not from the ticket level soldcount/soldamount,
  // because that is what a single event's summary card sums (see EventTicketsContainer, the
  // variations.reduce calls) and the group has to agree with the cards it is summing.
  // ticket.soldamount is derived from purchase.price instead and does not always match - one
  // group totalled 13,00 EUR against 1357,00 EUR of variation rows before this was fixed.
  // paid has no per variation equivalent, so it stays a sum of the ticket level field.
  return {
    eventcount: list.length,
    variations,
    purchases,
    payments,
    soldcount: variations.reduce((sum, v) => sum + v.soldcount, 0),
    ticketsavailable: variations.reduce((sum, v) => sum + v.ticketsavailable, 0),
    soldamount: variations.reduce((sum, v) => sum + v.soldamount, 0),
    paid: list.reduce((sum, t) => sum + (t.paid || 0), 0),
  }
}

// Quantity of one row when an admin adds tickets to a person (EventTicketsDialog ->
// eventtickets-addpurchases). The field is free text while typing, so it may be '', '0', '-2' or
// '1.5'; a row with quantity 0 (or an emptied field, which arrived as NaN -> null) used to reach
// the callable and create a ticket row nobody could use. Returns the whole number >= 1, or null
// when the value cannot be saved.
export const parseTicketQuantity = (value) => {
  const text = String(value === undefined || value === null ? '' : value).trim()
  if (!/^\d+$/.test(text)) return null
  const quantity = parseInt(text, 10)
  return quantity >= 1 ? quantity : null
}
